import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AdminStatus, PatientInLine, TRoomState } from "@/types/public";
import { RootState } from "../../store";
import { RoomEntity } from "@/graphql/generated";
import { loadFromLocalStorage, saveToLocalStorage } from "@/lib/utils";

const storedFocusedVid = loadFromLocalStorage("focusedVid");

const initialState: TRoomState = {
  id: null,
  title: null,
  hostIdentity: null,
  startTime: null,
  sessionName: null,
  memberCount: 0,
  patientLine: undefined,
  updatePatientLine: false,
  isInMeet: false,
  copied: "",
  stagedUserId: null,
  isMicOpen: false,
  isCamOpen: false,
  isShareOpen: false,
  isUserMuted: false,
  isUserWebCamMuted: false,
  docStat: false,
  focusedVidExist: false,
  unSeenFiles: 0,
  focusedVid: (storedFocusedVid as string) || null,
  showFileNotif: false,
  unreadMessges: [],
  roomData: undefined,
  focusOnChats: false,
  focusedhash: null,
  visitResults: null,
  wsConnected: false,
  wsConnecting: false,
  activeTab: "chats",
};

const slice = createSlice({
  name: "room",
  initialState,
  reducers: {
    setStagedUser: (state, action: PayloadAction<string>) => {
      state.stagedUserId = action.payload;
    },
    clearStagedUser: (state) => {
      state.stagedUserId = "";
    },
    getInMeet: (state) => {
      state.isInMeet = true;
    },
    getOutOfMeet: (state) => {
      state.isInMeet = false;
      state.focusedVid = null;
    },
    setHostIdentity: (state, action: PayloadAction<string>) => {
      state.hostIdentity = action.payload;
    },
    setMic: (state, action: PayloadAction<boolean>) => {
      state.isMicOpen = action.payload;
    },
    setShare: (state, action: PayloadAction<boolean>) => {
      state.isShareOpen = action.payload;
    },
    setCam: (state, action: PayloadAction<boolean>) => {
      state.isCamOpen = action.payload;
    },
    setCopied: (state, action: PayloadAction<string>) => {
      state.copied = action.payload;
    },
    setShowFileNotif: (state, action: PayloadAction<boolean>) => {
      state.showFileNotif = action.payload;
    },
    setMute: (state, action: PayloadAction<boolean>) => {
      state.isUserMuted = action.payload;
    },
    setMuteWebCam: (state, action: PayloadAction<boolean>) => {
      state.isUserWebCamMuted = action.payload;
    },
    setRoomData: (state, action: PayloadAction<RoomEntity>) => {
      state.roomData = action.payload;
    },
    setFocusOnChats: (state, action: PayloadAction<boolean>) => {
      state.focusOnChats = action.payload;
    },
    setUpdatePatientLine: (state, action: PayloadAction<boolean>) => {
      state.updatePatientLine = action.payload;
    },
    setBioLinkForPatient: (
      state,
      action: PayloadAction<{ userId: string; bioLink: string }>
    ) => {
      const { userId, bioLink } = action.payload;
      if (!state.patientLine) return;
      const patient = state.patientLine.find((p) => p.id === userId);
      if (patient) {
        patient.bioLink = bioLink;
      }
    },
    setPatientLine: (state, action: PayloadAction<PatientInLine[]>) => {
      state.patientLine = action.payload;
    },
    setWsConnectionStatus: (state, action: PayloadAction<boolean>) => {
      state.wsConnected = action.payload;
    },
    setDocStat: (state, action: PayloadAction<AdminStatus>) => {
      state.docStat = action.payload.toLocaleUpperCase() === "ONLINE";
    },
    setActiveTab: (
      state,
      action: PayloadAction<"patients_list" | "chats" | "files">
    ) => {
      state.activeTab = action.payload;
    },
    clearPatientLine: (state) => {
      state.patientLine = [];
    },
    setPatientFocus: (
      state,
      action: PayloadAction<{ hash: string; id: string }>
    ) => {
      state.focusedVid = action.payload.id;
      saveToLocalStorage("focusedVid", action.payload.id);
      state.focusedhash = action.payload.hash;
    },
    clearPatientFocus: (state) => {
      state.focusedVid = null;
      state.focusedhash = null;
      saveToLocalStorage("focusedVid", "");
    },
    setSessionName: (state, action: PayloadAction<string>) => {
      state.sessionName = action.payload;
    },
    setvisitResults: (
      state,
      action: PayloadAction<{
        test: boolean;
        pic: boolean;
        drug: boolean;
        rest: boolean;
      }>
    ) => {
      state.visitResults = { ...action.payload };
    },
    clearvisitResults: (state) => {
      state.visitResults = null;
    },
    setFocusedVidExist: (state, action: PayloadAction<boolean>) => {
      state.focusedVidExist = action.payload;
    },
    setUnreadMessage: (state, action: PayloadAction<{ gp: string }>) => {
      const found = state.unreadMessges.find(
        (item) => item.gp === action.payload.gp
      );
      if (found) found.count += 1;
      else {
        state.unreadMessges.push({
          gp: action.payload.gp.toString(),
          count: 1,
        });
      }
    },
    readMessage: (state, action: PayloadAction<string>) => {
      state.unreadMessges = state.unreadMessges.filter(
        (message) => message.gp.toString() !== action.payload
      );
    },
    setUnSeenFiles: (state, action: PayloadAction<number>) => {
      state.unSeenFiles = action.payload;
    },
  },
});

export const {
  getInMeet,
  getOutOfMeet,
  setStagedUser,
  clearStagedUser,
  setHostIdentity,
  setMute,
  setRoomData,
  setFocusOnChats,
  setPatientLine,
  clearPatientLine,
  setPatientFocus,
  clearPatientFocus,
  setSessionName,
  setMic,
  setvisitResults,
  clearvisitResults,
  setShare,
  setCam,
  setCopied,
  setShowFileNotif,
  setUpdatePatientLine,
  setDocStat,
  setBioLinkForPatient,
  setWsConnectionStatus,
  setUnreadMessage,
  readMessage,
  setActiveTab,
  setFocusedVidExist,
  setUnSeenFiles,
} = slice.actions;

export default slice.reducer;

export const selectCurrentRoom = (state: RootState) => state.room;
